import type { DragControls, PanInfo, Point } from 'motion/react';
import { useCallback, useRef } from 'react';

const SCROLL_THRESHOLD = 25; // How close to the edge of the scroll container to start scrolling
const SCROLL_AMOUNT = 5; // How many pixels to move per scroll event

export const useDraggableList = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollStartRef = useRef<number>(); // scrollTop when the drag starts
  const dragStartRef = useRef<number>(); // y position of the cursor when the drag starts
  const draggingEltControls = useRef<VisualElementDragControls>();

  const onScroll: React.UIEventHandler<HTMLDivElement> = useCallback((ev) => {
    if (!draggingEltControls.current) return;
    const startPoint = getDragStartPoint(draggingEltControls.current);
    const target = ev.target as HTMLElement | null;
    if (!(startPoint && target) || scrollStartRef.current === undefined || dragStartRef.current === undefined) {
      return;
    }
    const scrollDistance = target.scrollTop - scrollStartRef.current; // Distance from where the drag started
    startPoint.y = dragStartRef.current - scrollDistance; // Move the startPoint to account for the scroll
  }, []);

  const onDrag = useCallback((ev: Event, info: PanInfo) => {
    const scrollContainer = scrollerRef.current;
    if (!scrollContainer) return;
    const scrollContainerRect = scrollContainer.getBoundingClientRect();
    const dragPoint = info.point.y;

    // Check if target is the last elt in its parent container
    const eventTarget = ev.target;
    if (!(eventTarget instanceof Element)) return;

    const item = eventTarget.closest('[draggable]');
    const parent = item?.parentElement;
    if (!parent) return;

    if (dragPoint < scrollContainerRect.top + SCROLL_THRESHOLD && (item !== parent.firstElementChild || scrollContainer.scrollTop > 0)) {
      // User is dragging card to the top of the scroll container
      scrollContainer.scrollTop -= SCROLL_AMOUNT;
    } else if (dragPoint > scrollContainerRect.bottom - SCROLL_THRESHOLD && (item !== parent.lastElementChild || scrollContainer.scrollTop < scrollContainer.scrollHeight)) {
      // User is dragging card to the bottom of the scroll container
      scrollContainer.scrollTop += SCROLL_AMOUNT;
    }
  }, []);

  // Track the scroll distance by capturing scrollTop when the drag starts
  const onDragStart = useCallback(
    (dragControls?: any) => {
      const scroller = scrollerRef?.current;
      const controls = dragControls ? findDraggingElementControls(dragControls) : undefined;
      if (!scroller) return;
      if (!controls) return;
      draggingEltControls.current = controls;
      scrollStartRef.current = scroller.scrollTop;
      const startPoint = getDragStartPoint(controls);
      if (!startPoint) return;
      dragStartRef.current = startPoint.y;
    },
    [scrollerRef]
  );

  const onDragEnd = useCallback(() => {
    scrollStartRef.current = undefined;
    dragStartRef.current = undefined;
    draggingEltControls.current = undefined;
  }, []);

  return {
    // On the scrolling container
    scrollerRef,
    onScroll,
    // Event handlers for items
    onDrag,
    onDragStart,
    onDragEnd,
  };
};

type VisualElementDragControls = {
  panSession: {
    history: Point[];
  };
};

const findDraggingElementControls = (dragControls: DragControls) => {
  try {
    return Array.from<VisualElementDragControls>(
      // @ts-ignore - we're reaching into a private prop
      // https://github.com/framer/motion/blob/fb227f8b700c6e2d9c3b33d0a2af1a2d2b7849e9/packages/motion/react/src/gestures/drag/use-drag-controls.ts#L29
      dragControls.componentControls
    ).find((c: any) => c.isDragging);
  } catch (err) {
    return;
  }
};

const getDragStartPoint = (controls: VisualElementDragControls): Point | undefined => {
  try {
    // https://github.com/framer/motion/blob/fb227f8b700c6e2d9c3b33d0a2af1a2d2b7849e9/packages/motion/react/src/gestures/pan/PanSession.ts#L257
    return controls.panSession.history[0];
  } catch (err) {
    return;
  }
};
